import {
	LocalizationService,
	type SupportedLanguage
} from '@newo/localization';
import { useCallback, useMemo } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { Route, Routes, useSearchParams } from 'react-router-dom';
// components
import Layout from '@/components/Layout';
import Metadata from '@/components/Metadata';
import { PATHS, runtimeConfig } from '@/constants';
import { HostContextProvider } from '@/context/HostContext.tsx';
import useApp from '@/hooks/useApp.ts';
import { useLocalization } from '@/hooks/useLocalization.ts';
import AgentIntro from '@/pages/AgentIntro';
import AgentTraining from '@/pages/AgentTraining';
import BusinessConfirmation from '@/pages/BusinessConfirmation';
import CreatorChatFlow from '@/pages/CreatorChatFlow';
import { homeRoute } from '@/routes';
import { partnerMetadata } from '@/services/PartnerMetadata';
import StepperApp from '@/stepper/App';
import { CreatorRenderMode } from '@/types/creator.mf.ts';
import AgentSuccess from './pages/AgentSuccess';

const STEPPER_FLOW_QUERY_PARAM = 'flow';
const STEPPER_FLOW_QUERY_VALUE = 'stepper';

const App = () => {
	useApp();

	const [searchParams] = useSearchParams();
	const isStepperFlow =
		searchParams.get(STEPPER_FLOW_QUERY_PARAM) === STEPPER_FLOW_QUERY_VALUE;
	const localization = useLocalization();

	const onChangeLanguage = useCallback((value: string | null) => {
		if (!value) {
			return;
		}

		LocalizationService.setLanguage(value as SupportedLanguage);
	}, []);

	const providedLocalization = useMemo(() => {
		return {
			...localization,
			onChangeLanguage
		};
	}, [localization, onChangeLanguage]);

	return (
		<HostContextProvider
			value={{
				renderMode: CreatorRenderMode.Page,
				localization: providedLocalization
			}}
		>
			<HelmetProvider>
				<Metadata />
				{isStepperFlow ? (
					<StepperApp />
				) : (
					<Routes>
						{!runtimeConfig.isRemoteBuild &&
						partnerMetadata.isVibeCreatorConfirmationFlowEnabled() ? (
							<Route
								element={<Layout />}
								path={PATHS.creatorIndex}
							>
								<Route
									element={<AgentIntro />}
									index
								/>
								<Route
									element={<BusinessConfirmation />}
									path={PATHS.businessConfirmation}
								/>
								<Route
									element={<CreatorChatFlow />}
									path="voice"
								/>
							</Route>
						) : (
							<Route
								element={<Layout />}
								path={PATHS.creatorIndex}
							>
								<Route
									element={<AgentIntro />}
									index
								/>

								<Route
									element={<AgentTraining />}
									path="training"
								/>
								<Route
									element={<AgentSuccess />}
									path="success"
								/>
							</Route>
						)}

						<Route
							element={homeRoute.element}
							path={homeRoute.path}
						/>
					</Routes>
				)}
			</HelmetProvider>
		</HostContextProvider>
	);
};

export default App;
